import type {
  TClientAddContact,
  TClientAddContactApi,
  TLeasingBiometricSession,
  TLeasingBiometricSessionApi,
  TLeasingClient,
  TLeasingClientApi,
  TLeasingClientDetailed,
  TLeasingClientDetailedApi,
  TLeasingDocument,
  TLeasingDocumentApi,
  TLeasingFile,
  TLeasingFileApi,
  TLeasingOfferHistory,
  TLeasingOfferHistoryApi,
  TLeasingOfferHistoryUser,
  TLeasingOfferHistoryUserApi,
  TLeasingOrder,
  TLeasingOrderApi,
  TLeasingOrderDetailed,
  TLeasingOrderDetailedApi,
  TLeasingOrderHistory,
  TLeasingOrderHistoryApi,
  TLeasingOrderHistoryUser,
  TLeasingOrderHistoryUserApi,
  TLeasingOrderResponse,
  TLeasingOrderResponseApi,
  TLeasingSubject,
  TLeasingSubjectApi,
  TLeasingUser,
  TLeasingUserApi
} from '../types';

export const fromApi = (data: TLeasingOrderApi): TLeasingOrder => {
  return {
    autocreditRequestId: data.autocredit_request_id,
    client: parseClient(data.Client),
    clientFullName: data.client_full_name,
    companyName: data.company_name,
    contactPhoneNumber: data.contact_phone_number,
    createDate: data.create_date,
    iin: data.iin,
    initialPayment: data.initial_payment,
    isClientIpOwner: data.is_client_ip_owner,
    requestTypeCode: data.Request_Type?.code || null,
    status: data.status,
    totalAmount: data.total_amount,
    user: parseUser(data.user),
    uuid: data.uuid
  };
};

export const fromDetailedApi = (
  data: TLeasingOrderDetailedApi
): TLeasingOrderDetailed => {
  return {
    applicationData: data.application_data,
    bankRequisits: data.bank_requisits,
    biometricSession: parseBiometricSession(data.biometric_session),
    client: parseClientDetailed(data.Client),
    clientFullName: data.client_full_name,
    code: data.code,
    comments: data.Requests_Comments,
    companyName: data.company_name,
    contactPhoneNumber: data.contact_phone_number,
    dealerUuid: data.dealer_uuid,
    docName: parseDocument(data.doc_name),
    documentIssueDate: data.document_issue_date,
    documentNumber: data.document_number,
    documentValidEndDate: data.document_valid_end_date,
    email: data.email,
    firstSupervisorFullName: data.first_supervisor_fullName,
    history: data.History.map(parseHistory),
    iin: data.iin,
    initialPayment: data.initial_payment,
    leasingSubjects: data.Leasing_Subjects.map(parseLeasingSubject),
    legalAddress: data.legal_address,
    licenseInformation: data.license_information,
    licenseNum: data.license_num,
    licenseOrganizationAuthority: data.license_organization_authority,
    mainActivity: data.main_activity,
    okedOfMainAndSubActivity: data.oked_of_main_and_sub_activity,
    period: data.period,
    placeOfRegistration: data.place_of_registration,
    propertyType: data.property_type,
    registrationCountry: data.registration_country,
    responses: data.responses.map(parseResponse),
    stateRegistrationDate: data.state_registration_date,
    stateRegistrationNumber: data.state_registration_number,
    status: data.status,
    totalAmount: data.total_amount,
    user: parseUser(data.user),
    uuid: data.uuid,
    workStartDate: data.work_start_date
  };
};

export const parseHistory = (
  data: TLeasingOrderHistoryApi
): TLeasingOrderHistory => {
  return {
    action: data.action,
    comment: data.comment,
    createDate: data.create_date,
    user: data.user ? parseHistoryUser(data.user) : null
  };
};

const parseHistoryUser = (
  data: TLeasingOrderHistoryUserApi
): TLeasingOrderHistoryUser => {
  return {
    firstName: data.first_name,
    lastName: data.last_name,
    middleName: data.middle_name
  };
};
export const parseFileFromApi = (data: TLeasingFileApi): TLeasingFile => {
  return {
    createDate: data.create_date,
    link: data.link,
    name: data.name,
    type: data.type,
    uuid: data.uuid
  };
};

const parseUser = (data: TLeasingUserApi): TLeasingUser => {
  return {
    email: data.email,
    firstName: data.first_name,
    lastName: data.last_name,
    middleName: data.middle_name,
    uuid: data.uuid
  };
};

const parseClient = (data: TLeasingClientApi | null): TLeasingClient | null => {
  if (!data) return null;
  return {
    clientIin: data.client_iin,
    firstName: data.first_name,
    lastName: data.last_name,
    middleName: data.middle_name,
    phoneNumber: data.phone_number,
    titleCompany: data.title_company
  };
};

const parseClientDetailed = (
  data: TLeasingClientDetailedApi | null
): TLeasingClientDetailed | null => {
  if (!data) return null;
  const addContact = data.Client_Add_Contacts?.[0];
  return {
    addContact: addContact ? parseClientAddContact(addContact) : null,
    additionalIncome: data.additional_income,
    birthDate: data.birth_date,
    childCount: data.child_count,
    clientIin: data.client_iin,
    documentType: data.document_type,
    firstName: data.first_name,
    gender: data.gender,
    jobTitle: data.job_title,
    lastName: data.last_name,
    maritalStatus: data.marital_status,
    middleName: data.middle_name,
    phoneNumber: data.phone_number,
    salary: data.salary,
    socialStatus: data.social_status,
    workAddress: data.work_address,
    workTelNumber: data.work_tel_number
  };
};

const parseDocument = (
  data: TLeasingDocumentApi | null
): TLeasingDocument | null => {
  if (!data) return null;
  return {
    code: data.code,
    codeBcc: data.code_bcc,
    name: data.name,
    uuid: data.uuid
  };
};

export const parseResponse = (
  data: TLeasingOrderResponseApi
): TLeasingOrderResponse => {
  return {
    approvedAmount: data.approved_amount,
    balloonAmount: data.balloon_amount,
    comment: data.comment,
    firstMonthPayment: data.first_month_payment,
    guaranteeLetterFileUrl: data.guarantee_letter_file_url,
    history: data.Leasing_Responses_History.map(parseOfferHistory),
    initialPayment: data.initial_payment,
    insuranceAmount: data.insurance_amount,
    invoiceLink: data.invoice_link,
    isAlternativeOffer: data.is_alternative_offer,
    leasingCompany: data.Leasing_Company,
    loanPeriod: data.loan_period,
    paymentOrderFileUrl: data.payment_order_file_url,
    purchaseContractNumber: data.purchase_contract_number,
    rate: data.rate,
    salesDocumentSigningLink: data.sales_document_signing_link,
    status: data.status,
    telematicsAmount: data.telematics_amount,
    uuid: data.uuid
  };
};

const parseOfferHistory = (
  data: TLeasingOfferHistoryApi
): TLeasingOfferHistory => {
  return {
    comment: data.comment,
    createdAt: data.created_at,
    fromStatus: data.from_status,
    responseUuid: data.response_uuid,
    toStatus: data.to_status,
    user: parseOfferHistoryUser(data.user),
    uuid: data.uuid
  };
};

const parseOfferHistoryUser = (
  data: TLeasingOfferHistoryUserApi | null
): TLeasingOfferHistoryUser | null => {
  if (!data) return null;
  return {
    email: data.email,
    firstName: data.first_name,
    lastName: data.last_name,
    middleName: data.middle_name,
    phoneNumber: data.phone_number
  };
};

const parseLeasingSubject = (data: TLeasingSubjectApi): TLeasingSubject => {
  return {
    brand: data.brand,
    cost: data.cost,
    engineType: data.Engine_Type,
    equipmentName: data.equipmentName,
    equipmentUuid: data.equipment_uuid,
    model: data.model,
    quantity: data.quantity,
    uuid: data.uuid,
    vinCodes: data.vin_codes,
    year: data.year
  };
};

const parseBiometricSession = (
  data: TLeasingBiometricSessionApi | null
): TLeasingBiometricSession | null => {
  if (!data) return null;
  const { last_event_name: lastEventName } = data;
  return {
    isExpired: lastEventName === 'expired',
    isLoading: lastEventName !== 'expired' && lastEventName !== 'flow.end',
    isValidated: data.is_validated,
    lastEventName: data.last_event_name
  };
};

const parseClientAddContact = (
  data: TClientAddContactApi
): TClientAddContact => {
  return {
    addContactsIsRelated: data.add_contacts_is_related,
    fullName: data.full_name,
    phoneNumber: data.phone_number
  };
};
